import { useParams } from "react-router-dom"; // Import useParams for dynamic routing
import { FaCheckCircle } from "react-icons/fa"; // Import the Font Awesome icon
import HeaderSection from "../components/shared/HeaderSection"; // Import the reusable HeaderSection component
import { motion } from "framer-motion";
import AnimatedButton from "../components/shared/AnimatedButton";

const ServiceTypes = () => {
  const { serviceType } = useParams(); // Get the dynamic serviceType from the URL

  // Define the services data
  const servicetypes = {
    "internet-services": {
      title: "Internet Services",
      sub: "Connecting Your Business to the Digital World",
      description1:
        "Hither Tech offers a comprehensive suite of internet services designed to establish and maintain a strong online presence. Our services include:",
      description2: [
        "Domain Registration – Secure and professional domain acquisition tailored to your brand identity.",
        "Web Hosting – Reliable, fast, and scalable hosting solutions optimized for performance and uptime.",
        "Business Email Solutions – Secure email hosting with professional-grade features, enhancing communication and credibility.",
        "Cloud Services – Scalable cloud infrastructure for growing businesses.",
        "Network Solutions – Enterprise-grade networking and connectivity services.",
      ],
      description3:
        "We ensure each component is seamlessly integrated, providing a secure and stable digital foundation for your business.",
      image: "/images/services/internet-services.jpg",
    },
    "software-solutions": {
      title: "Software Solutions",
      sub: "Custom Development for Your Unique Business Needs",
      description1:"Our software development services are crafted to deliver tailor-made solutions that align with your operational goals and technical requirements. We specialize in:",
      description2: [
        "Web and Desktop Application Development – User-friendly, scalable applications designed to enhance functionality and productivity.",
        "API Development and Integration – Enabling smooth communication between different systems and platforms.",
        "System Automation – Streamlining repetitive tasks to improve efficiency and reduce human error.",
        "Custom web applications – Tailored web applications designed to meet your specific business requirements.",
        "Database Solutions – Robust database design, migration, and optimization"
      ],
      description3: "Each solution is built with scalability, security, and performance in mind.",
      image: "/images/services/software-solutions.jpg",
    },
    "mobile-app-development": {
      title: "Mobile App Development",
      sub: "Bringing Your Ideas to Life, One App at a Time",
      description1:"In a mobile-first world, Hither Tech develops dynamic mobile applications that offer seamless user experiences. We provide:",
      description2: [
        "Native App Development – Optimized for Android and iOS, ensuring high performance and full hardware integration.",
        "Cross-Platform Solutions – Developed using modern frameworks to maintain consistent performance across devices.",
        "Custom Features and UI/UX Design – Engaging interfaces and intuitive navigation tailored to your users' needs.",
        "App Maintenance and Update – Ongoing support and enhancement services",
      ],
      description3: "We ensure that your mobile application not only meets current market expectations but also exceeds them.",
      image: "/images/services/mobile-app-development.jpg",
    },
    "web-app-development": {
      title: "Web App Development",
      sub: "Building Powerful Digital Experiences",
      description1:"Your web application is often the first impression of your business. We develop secure, scalable, and high-performing web applications that align with your business goals. Our services include:",
      description2: [
        "Custom Web Applications – Purpose-built platforms designed to address your specific business challenges.",
        "Responsive Website Development – Mobile-first, fully adaptive websites for a consistent user experience across all devices.",
        "E-Commerce Solutions – Sales-optimized online stores with secure payments, inventory management, and customer tools.",
        "Front-End Excellence – Interactive interfaces built using Modern frontend technologies for speed and engagement.",
        "Back-End Stability – Scalable server-side architecture to support your business operations.",
      ],
      image: "/images/services/web-app-development.jpg",
    },
    "erp-consultation": {
      title: "ERP Consultation & Support",
      sub: "Optimizing Enterprise Operations Through Integrated Solutions",
      description1:"We offer comprehensive Enterprise Resource Planning (ERP) consulting services that empower businesses to unify their operations. Our expertise includes:",
      description2: [
        "ERP Implementation – Tailored deployment strategies that align with your company’s workflow.",
        "Customization and Configuration – Modifying systems to meet specific organizational needs.",
        "Continuous Support and Training – Ensuring seamless adoption, optimal use, and long- term sustainability.",
        "Performance Optimization – Fine-tuning your ERP system for maximum efficiency.",
        "Data Migration – Seamless transfer of your business data to new systems.",
      ],
      description3: "Our consultants work closely with clients to deliver ERP systems that are robust, efficient, and scalable.",
      image: "/images/services/erp-consultation.jpg",
    },
    "cctv-&-surveillance-solutions": {
      title: "CCTV & Surveillance Solutions",
      sub: "Enhancing Safety Through Intelligent Monitoring",
      description1:"Security starts with visibility. At Hither Tech, our CCTV & Surveillance Solutions provide comprehensive monitoring systems tailored to safeguard your premises, people, and property. Our services include:",
      description2: [
        "CCTV Camera Installation & Configuration – Professional setup of high-definition surveillance systems suited to your environment.",
        "Remote Monitoring Systems – Real-time access to your security feeds from anywhere, anytime.",
        "Maintenance & Upgrades – Regular servicing and technology updates to keep your system reliable and effective.",
        "Integrated Security Solutions – Seamless integration with alarms, access control, and other security systems for a unified safety network.",
        "Site Assessments – Expert evaluation to determine optimal camera placement and system design.",
        "Smart Surveillance Features – Motion detection, facial recognition, and AI-based alerts for proactive threat identification.",
        "Data Storage & Retrieval – Secure and scalable storage solutions for video data, with quick access when needed.",
      ],
      description3: "Our CCTV systems are designed not just to record incidents but to deter them—giving you peace of mind and control in a constantly changing world.",
      image: "/images/services/cctv-solutions.jpg",
    },
    "hardware-solutions": {
      title: "Hardware Solutions",
      sub: "Reliable Infrastructure for Sustainable Growth",
      description1:"Hither Tech offers full-spectrum hardware services that ensure your physical infrastructure is as strong as your software. Our offerings include:",
      description2: [
        "Installation and Setup – Professional configuration and deployment of IT hardware.",
        "Maintenance and Upgrades – Keeping systems up-to-date and running at peak performance.",
        "Secure Recycling – Environmentally responsible disposal of outdated or redundant equipment.",
        "Hardware Procurement – Strategic sourcing of optimal equipment.",
        "Secure Hardware Recycling – Environmentally responsible disposal of old equipment."
      ],
      description3: "We provide dependable hardware solutions that support the long-term technological needs of your business.",
      image: "/images/services/hardware-solutions.jpg",
    },
    "security-solutions": {
      title: "Security Solutions",
      sub: "Safeguarding Your Business in the Digital Era",
      description1:"Security is a core priority in all our offerings. Hither Tech delivers state-of-the-art cybersecurity services designed to protect your business assets and sensitive data. These include:",
      description2: [
        "Vulnerability Assessments – Identifying and addressing security risks before they become threats.",
        "Data Protection Protocols – Ensuring the confidentiality, integrity, and availability of your information.",
        "Secure Data Erasure – Certified removal of sensitive data from storage devices.",
        "Security Audits – Thorough assessment of your security posture.",
        "Compliance Solutions – Helping you meet industry-specific regulations"
      ],
      description3: "Our proactive security strategies help ensure your organization remains compliant and protected in a complex digital environment.",
      image: "/images/services/security-solutions.jpg",
    },
  };

  // Get the service data based on the serviceType
  const service = servicetypes[serviceType];

  // If the serviceType is invalid, show a "not found" message
  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <h1 className="text-3xl font-bold text-gray-800">Service not found.</h1>
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-100 text-gray-800">
      {/* Header Section */}
      <HeaderSection
        image={service.image}
        header={service.title}
        subHeader={service.sub}
        bgBlack={50}
      />

      {/* Main Content */}
      <div className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          {/* Description Section */}
          <div className="bg-white p-10 rounded-lg shadow-lg">
            <p className="text-lg sm:text-xl font-medium text-gray-700 mb-8">
              {service.description1}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.description2.map((item, index) => {
                // Only split if " – " (en dash with spaces) is present
                if (item.includes(" – ")) {
                  const [boldPart, ...rest] = item.split(" – ");
                  return (
                    <div
                      key={index}
                      className="flex items-start space-x-4 bg-gray-50 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                    >
                      <FaCheckCircle className="text-blue-600 w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
                      <span className="text-gray-800 text-md sm:text-lg">
                        <strong>{boldPart}</strong> – {rest.join(" – ")}
                      </span>
                    </div>
                  );
                }
                // If no " – " present, render the whole item normally
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-4 bg-gray-50 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                  >
                    <FaCheckCircle className="text-blue-600 w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
                    <span className="text-gray-800 text-md sm:text-lg">
                      {item}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-md sm:text-lg leading-relaxed text-gray-700 mt-8">
              {service.description3}
            </p>
          </div>

          <motion.div 
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <AnimatedButton 
              to="/quote" 
              text="Request a Quote"            />
          </motion.div>
          
        </div>
      </div>

    </section>
  );
};

export default ServiceTypes;